import React, { useState } from "react";
import { FaUser, FaUsers, FaCheck, FaUserFriends, FaTimes } from 'react-icons/fa';
import LoadingForButton from "./LoadingForButton";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from "./ConfirmModal";
import GuestService from "../services/guestService";
import { refreshDataGuest } from '../store/guestSlice';
import { useTranslation } from "react-i18next";

const TablesReservation = ({ tables, wedding, setShowNotification, setNotificationMessage, setErrorResponse, handleCancelGroup }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [message, setMessage] = useState('');
    const [limitMessage, setLimitMessage] = useState('');
    const [isCancel, setIsCancel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [cancelGroupLoading, setCancelGroupLoading] = useState(false);
    const [showCancelGroupConfirm, setShowCancelGroupConfirm] = useState(false);

    const guest = useSelector((state) => state.guestStore.guest);
    const dispatch = useDispatch();

    const getLockDate = (wedding) => {
        const weddingDate = new Date(wedding.date);
        const lockDate = new Date(weddingDate);
        lockDate.setDate(lockDate.getDate() - (wedding.lockReservationDaysBefore || 3));
        return lockDate;
    };

    const getDaysUntilLock = (wedding) => {
        const lockDate = getLockDate(wedding);
        const now = new Date();
        const diffTime = lockDate - now;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const canGuestEditReservation = (wedding) => new Date() < getLockDate(wedding);
    const daysLeft = getDaysUntilLock(wedding);
    const canEdit = canGuestEditReservation(wedding);

    const capitalizeWords = (str) =>
        str?.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    const unregistered = guest.companions?.unregistered || 0;
    const registered = guest.companions?.registered || [];
    const isChangingTable = !!guest.reservedTableId;

    const registeredWithoutTable = !isChangingTable
        ? registered.filter(r => !r.reservedTableId)
        : [];

    const seatsNeeded = 1 + unregistered + registeredWithoutTable.length;
    const hasGroup = unregistered > 0 || registered.length > 0;

    const handleReserveTable = (tableId) => {
        if (!canEdit) {
            setNotificationMessage(t('reservationLockedMessage'));
            setShowNotification(true);
            setErrorResponse(true);
            setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 3000);
            return;
        }
        const tableName = tables.find(t => t._id === tableId).name;
        setSelectedTableId(tableId);

        if (isChangingTable) {
            const lines = [`Promijeniti stol na "${tableName}"?`];
            lines.push(`• Vi prelazite za novi stol.`);
            if (unregistered > 0) {
                lines.push(`• ${unregistered} dodatnih mjesta prelazi zajedno s vama.`);
            }
            if (registered.length > 0) {
                lines.push(`• Gosti s pozivnicom ostaju za trenutnim stolom.`);
            }
            lines.push(`Jeste li sigurni?`);
            setMessage(lines.join('\n'));
        } else if (hasGroup) {
            const groupLines = [`Rezervirati stol "${tableName}"?`, ''];
            groupLines.push(`👤 ${capitalizeWords(guest.fullName)} (ti)`);
            registeredWithoutTable.forEach(r => {
                groupLines.push(`👤 ${capitalizeWords(r.fullName || String(r))}`);
            });
            if (unregistered > 0) {
                groupLines.push(`+ ${unregistered} dodatnih mjesta`);
            }
            groupLines.push('');
            groupLines.push(`Ukupno: ${seatsNeeded} ${seatsNeeded === 1 ? 'osoba' : seatsNeeded < 5 ? 'osobe' : 'osoba'}`);
            setMessage(groupLines.join('\n'));
        } else {
            setMessage(`${t('confirmReserveTable')}: ${tableName}?`);
        }
        setIsModalOpen(true);
    };

    const handleCancelReservation = () => {
        if (!canEdit) {
            setNotificationMessage(t('reservationLockedMessage'));
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
            return;
        }
        setMessage(t('confirmCancelReservation'));
        setIsModalOpen(true);
        setIsCancel(true);
    };

    const handleConfirmAction = () => {
        setIsModalOpen(false);
        setIsLoading(true);

        if (isCancel) {
            GuestService.cancelReservation()
                .then(() => {
                    dispatch(refreshDataGuest());
                    setNotificationMessage(t('reservationCancelled'));
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                    setSelectedTableId(null);
                    setIsLoading(false);
                    setIsCancel(false);
                })
                .catch((err) => {
                    if (err.response?.data?.message === 'You have reached the maximum number of cancellations') {
                        setLimitMessage(t("cancelLimitReached"));
                    }
                    setErrorResponse(true);
                    setTimeout(() => setErrorResponse(false), 3000);
                    setSelectedTableId(null);
                    setIsLoading(false);
                    setIsCancel(false);
                });
            return;
        }

        GuestService.reserveSeat({ tableId: selectedTableId })
            .then(() => {
                dispatch(refreshDataGuest());
                setNotificationMessage(t('tableReservedSuccess'));
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                setSelectedTableId(null);
                setIsLoading(false);
            })
            .catch((err) => {
                const msg = err.response?.data?.message || t('genericError');
                setNotificationMessage(msg);
                setShowNotification(true);
                setErrorResponse(true);
                setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 3000);
                setSelectedTableId(null);
                setIsLoading(false);
            });
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTableId(null);
        setMessage('');
        setIsCancel(false);
    };

    const handleConfirmCancelGroup = () => {
        setCancelGroupLoading(true);
        setShowCancelGroupConfirm(false);
        handleCancelGroup();
    };

    return (
        <div className="flex w-full p-4">

            {/* ── Modal: Izađi iz grupe ── */}
            {showCancelGroupConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-sm text-center space-y-4">
                        <FaUserFriends className="text-gray-400 text-3xl mx-auto" />
                        <h3 className="text-lg font-semibold text-gray-800">Izlaz iz grupe</h3>
                        <p className="text-sm text-gray-500">
                            Izlaskom iz grupe vaša rezervacija stola bit će poništena. Moći ćete samostalno odabrati stol.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={handleConfirmCancelGroup}
                                disabled={cancelGroupLoading}
                                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition"
                            >
                                {cancelGroupLoading ? <LoadingForButton /> : 'Izađi iz grupe'}
                            </button>
                            <button
                                onClick={() => setShowCancelGroupConfirm(false)}
                                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                            >
                                Odustani
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="rounded-md w-full">
                {/* ── Info banner: gost dodan od drugog gosta — izvan flex reda ── */}
                {guest.addedByGuestName && !guest.rsvpDate && (
                    <div className="mb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-start gap-3 mb-3">
                                <FaUserFriends className="text-blue-400 text-xl flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-semibold text-blue-700">Dio ste grupe</p>
                                    <p className="text-xs text-blue-500 mt-0.5">
                                        Za promjenu stola ili poništenje rezervacije potrebno je prvo izaći iz grupe.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowCancelGroupConfirm(true)}
                                disabled={cancelGroupLoading}
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                            >
                                {cancelGroupLoading ? <LoadingForButton /> : 'Izađi iz grupe'}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Header info red ── */}
                <div className="flex flex-col gap-3 mb-4">
                    {guest?.reservedTableId ? (
                        <h2 className="text-xl">
                            {t("currentTable", { name: tables?.find(table => table._id === guest?.reservedTableId)?.name })}
                        </h2>
                    ) : (
                        <h2 className="text-xl">{t("noTableReserved")}</h2>
                    )}

                    {/* Info o grupi — prikazuje se samo kod prve rezervacije */}
                    {hasGroup && !isChangingTable && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">
                            <p className="font-semibold mb-1 flex items-center gap-2">
                                <FaUserFriends /> Rezerviraš za grupu ({seatsNeeded} osoba)
                            </p>
                            <ul className="flex flex-col gap-1 mt-1">
                                <li className="flex items-center gap-2 text-rose-600">
                                    <FaUser className="text-xs" />
                                    <span className="capitalize">{capitalizeWords(guest.fullName)}</span>
                                    <span className="text-xs text-gray-400">(ti)</span>
                                </li>
                                {registered.map((r) => (
                                    <li key={r._id || r} className="flex items-center gap-2 text-rose-600">
                                        <FaUser className="text-xs" />
                                        <span className="capitalize">{capitalizeWords(r.fullName || String(r))}</span>
                                        {r.reservedTableId && (
                                            <span className="text-xs text-gray-400">(već ima stol)</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {unregistered > 0 && (
                                <p className="mt-1 text-rose-600">+ {unregistered} dodatna mjesta</p>
                            )}
                        </div>
                    )}

                    {/* Info o promjeni stola */}
                    {isChangingTable && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                            <p className="flex items-center gap-2">
                                ⚠️ Promjena stola povećava broj otkazivanja.
                                {unregistered > 0 && ` Vaša dodatna mjesta (${unregistered}) bit će premještena zajedno s vama.`}
                                {registered.length > 0 && ' Gosti koje ste smjestili ostaju za trenutnim stolom.'}
                            </p>
                        </div>
                    )}

                    {/* Deadline za rezervaciju */}
                    {!guest?.reservedTableId && (
                        canEdit ? (
                            <div className={`p-4 rounded-lg border-l-4 ${daysLeft <= 3 ? 'bg-orange-50 border-orange-500' : 'bg-blue-50 border-blue-500'}`}>
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">⏰</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-1">{t('reservationDeadline')}</h4>
                                        <p className={daysLeft <= 3 ? 'text-orange-700' : 'text-blue-700'}>
                                            {daysLeft > 0
                                                ? t('daysUntilLock', { days: daysLeft, date: getLockDate(wedding).toLocaleDateString() })
                                                : t('lastDayToReserve')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">🔒</span>
                                    <div>
                                        <h4 className="font-semibold text-red-800 mb-1">{t('reservationsLocked')}</h4>
                                        <p className="text-red-700">{t('reservationLockedMessage')}</p>
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Cancel count upozorenje */}
                    {guest?.reservationCancelCount >= 2 && (
                        <div className={`flex flex-wrap justify-between sm:flex-row sm:gap-7 text-sm md:text-xl font-medium px-4 py-2 rounded-xl
                            ${guest?.reservationCancelCount >= 5 ? 'bg-red-200 text-red-800'
                                : guest?.reservationCancelCount >= 3 ? 'bg-yellow-200 text-yellow-800'
                                    : 'bg-green-100 text-green-800'}`}>
                            {t("reservationCancelInfo", {
                                count: guest?.reservationCancelCount || 0,
                                remaining: 5 - (guest?.reservationCancelCount || 0)
                            })}
                            {guest?.reservationCancelCount >= 5 && <p>{t("reservationLimitReached")}</p>}
                        </div>
                    )}
                </div>

                {tables?.length === 0 && (
                    <div className="w-full text-center text-gray-500 text-lg py-10">{t("noTables")}</div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {tables?.filter((table) => {
                        const isCurrentTable = String(guest?.reservedTableId) === String(table._id);
                        if (isCurrentTable) return true;
                        if (table.isLocked) return false;
                        const seatsForThisTable = isChangingTable ? 1 + unregistered : seatsNeeded;
                        const tableFreeSeats = table.seats - table.reservedSeats;
                        if (tableFreeSeats < seatsForThisTable) return false;
                        return true;
                    }).map((table) => {
                        const tableFreeSeats = table.seats - table.reservedSeats;
                        const isCurrentTable = String(guest?.reservedTableId) === String(table._id);

                        const seatsForThisTable = isChangingTable
                            ? 1 + unregistered
                            : seatsNeeded;

                        const canReserve = !isCurrentTable &&
                            !table.isLocked &&
                            !guest?.onLockedTable &&
                            tableFreeSeats >= seatsForThisTable &&
                            canEdit;

                        return (
                            <div
                                key={table._id}
                                className={`flex flex-col ${isCurrentTable
                                    ? 'bg-blue-300 border-blue-500'
                                    : 'bg-white border border-gray-500'
                                    } rounded-xl p-4 shadow-md hover:shadow-lg transition-all border`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h2 className="text-2xl italic font-semibold capitalize">
                                        {t('tableLabel', { name: table.name })}
                                    </h2>
                                </div>

                                <div className="flex justify-between items-center gap-4 text-gray-800 text-md mb-4 mt-2">
                                    <div className="flex justify-center items-center">
                                        <FaUsers className="mr-2" />
                                        <span>{table.reservedSeats}</span>
                                        <span>/</span>
                                        <span>{t('seatsLabel', { seats: table.seats })}</span>
                                    </div>
                                    <div className="bg-green-200 text-green-900 text-sm px-3 py-1 rounded-full">
                                        {t('freeSeatsLabel', { free: tableFreeSeats })}
                                    </div>
                                </div>

                                <div className="flex-1 mb-6">
                                    <h1 className="mb-2">{t("guestsAtTable")}</h1>
                                    {table.reservedSeats > 0 ? (
                                        <ul className="space-y-2">
                                            {table.guests.map((g, index) => (
                                                <li key={index} className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2 text-gray-900">
                                                        <FaUser /> {capitalizeWords(g.fullName)}
                                                    </div>
                                                    {g.companions?.unregistered > 0 && (
                                                        <div className="flex items-center gap-2 text-gray-500 text-sm pl-6">
                                                            <FaUser className="text-xs" />
                                                            + {g.companions.unregistered} dodatna mjesta
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-center text-gray-500">{t("noGuestsAtTable")}</p>
                                    )}
                                </div>

                                <div className={`flex flex-col gap-2 ${guest?.reservationCancelCount >= 5 ? 'hidden' : ''}`}>

                                    {/* Gost je u grupi i nije potvrdio dolazak — samo prikaz, bez akcija */}
                                    {!guest.rsvpDate || !guest.isAttending ? (
                                        isCurrentTable && (
                                            <div className="flex items-center justify-center bg-white text-gray-800 p-3 rounded-xl gap-2">
                                                <FaCheck className="text-green-500" /> {t("yourCurrentTable")}
                                            </div>
                                        )
                                    ) : (
                                        <>
                                            {canReserve && (
                                                <button
                                                    onClick={() => handleReserveTable(table._id)}
                                                    disabled={!canEdit}
                                                    className={`submitButton w-full flex items-center justify-center gap-2 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isLoading && selectedTableId === table._id ? (
                                                        <LoadingForButton />
                                                    ) : isChangingTable ? (
                                                        'Premjesti se za ovaj stol'
                                                    ) : hasGroup ? (
                                                        <><FaUserFriends /> Rezerviraj za grupu ({seatsNeeded})</>
                                                    ) : (
                                                        t("chooseThisTable")
                                                    )}
                                                </button>
                                            )}

                                            {isCurrentTable && (
                                                <div className="flex flex-col items-center bg-white text-gray-800 p-3 rounded-xl gap-2">
                                                    <span className="flex items-center gap-1">
                                                        <FaCheck className="text-green-500" /> {t("yourCurrentTable")}
                                                    </span>
                                                    <button
                                                        onClick={() => handleCancelReservation()}
                                                        className={`${guest?.reservationCancelCount >= 4 || guest?.onLockedTable ? 'hidden' : ''} text-red-600 text-sm underline hover:text-red-800 transition`}
                                                    >
                                                        {t("cancelReservation")}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <ConfirmModal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                onConfirm={handleConfirmAction}
                message={message}
            />
        </div>
    );
};

export default TablesReservation;
